<script>
  import { workspace } from '../../lib/stores/workspace.js';
  import { supabase } from '../../lib/supabase.js';
  import { showToast } from '../../lib/stores/toast.js';
  import dagre from 'dagre';

  let { activeJourneyIds = [], activePageIds = [], activeButtonIds = [], onEditJourney, edgeStyle = 'curved', registerTidy, oneditpage } = $props();

  let pages = $state([]);
  let buttons = $state([]);
  let choices = $state([]);
  let journeys = $state([]);
  let loading = $state(true);

  let zoom = $state(1);
  let panX = $state(0);
  let panY = $state(0);
  let isPanning = $state(false);
  let panStart = { x: 0, y: 0, panX: 0, panY: 0 };

  let routeEditor = $state(null);
  let routeJourneyId = $state(null);

  let pageDragState = $state(null);
  const DRAG_THRESHOLD = 5;

  function onPageMouseDown(e, node) {
    if (e.target.closest('.tc-btn-chip') || e.target.closest('.tc-route-editor')) return;
    if (e.button !== 0) return;
    e.stopPropagation();
    pageDragState = {
      pageId: node.page.id,
      startX: e.clientX,
      startY: e.clientY,
      startPageX: node.x,
      startPageY: node.y,
      isDragging: false,
      hasMoved: false
    };
    window.addEventListener('mousemove', onPageMouseMove);
    window.addEventListener('mouseup', onPageMouseUp);
  }

  function onPageMouseMove(e) {
    if (!pageDragState) return;
    const dx = e.clientX - pageDragState.startX;
    const dy = e.clientY - pageDragState.startY;
    if (!pageDragState.isDragging && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
      pageDragState.isDragging = true;
      pageDragState.hasMoved = true;
    }
    if (pageDragState.isDragging) {
      const idx = pages.findIndex(p => p.id === pageDragState.pageId);
      if (idx !== -1) {
        const canvasDx = dx / zoom;
        const canvasDy = dy / zoom;
        pages[idx] = {
          ...pages[idx],
          manual_position_x: pageDragState.startPageX + canvasDx + PAGE_W / 2,
          manual_position_y: pageDragState.startPageY + canvasDy + pageHeight(pageDragState.pageId) / 2
        };
        pages = [...pages];
      }
    }
  }

  async function onPageMouseUp(e) {
    window.removeEventListener('mousemove', onPageMouseMove);
    window.removeEventListener('mouseup', onPageMouseUp);
    if (!pageDragState) return;

    if (!pageDragState.hasMoved) {
      oneditpage?.(pageDragState.pageId);
    } else {
      const page = pages.find(p => p.id === pageDragState.pageId);
      if (page) {
        await supabase.from('bb_tech_pages').update({
          manual_position_x: page.manual_position_x,
          manual_position_y: page.manual_position_y
        }).eq('id', pageDragState.pageId);
      }
    }
    pageDragState = null;
  }

  const PAGE_W = 240;
  const PAGE_H_BASE = 90;
  const BTN_H = 22;

  async function load() {
    if (!workspace.id) return;
    loading = true;
    try {
      const [pgs, btns, chs, jys] = await Promise.all([
        supabase.from('bb_tech_pages').select('*').eq('workspace_id', workspace.id).is('deleted_at', null),
        supabase.from('bb_tech_buttons').select('id, page_id, name, description, is_hidden, sort_order').eq('workspace_id', workspace.id).is('deleted_at', null).order('sort_order', { ascending: true }),
        supabase.from('bb_tech_choices').select('*').eq('workspace_id', workspace.id).is('deleted_at', null),
        supabase.from('bb_tech_journeys').select('*').eq('workspace_id', workspace.id).is('deleted_at', null)
      ]);
      pages = pgs.data || [];
      buttons = btns.data || [];
      choices = chs.data || [];
      journeys = jys.data || [];
    } catch (e) {
      showToast('Tech canvas load failed: ' + e.message, 'error');
    } finally {
      loading = false;
    }
  }

  $effect(() => { if (workspace.id) load(); });

  // Heatmap: a page with a step_key ('offer:step') shows that step's real numbers from BLV Insight
  // (fn_insight_offer_funnel, last 30 days). Admin-only on the server; for anyone else it quietly shows nothing.
  let heatOn = $state(true);
  let heat = $state({});   // 'offer:step' → { reached, left_here, kind }
  async function loadHeat() {
    const offers = [...new Set(pages.map(p => p.step_key?.split(':')[0]).filter(Boolean))];
    const next = {};
    await Promise.all(offers.map(async (o) => {
      const { data, error } = await supabase.rpc('fn_insight_offer_funnel', { p_offer: o, p_days: 30 });
      if (error || !data) return;
      for (const st of data.steps || []) next[`${o}:${st.k}`] = st;
    }));
    heat = next;
  }
  $effect(() => { if (heatOn && pages.some(p => p.step_key)) loadHeat(); });
  function heatFor(page) {
    if (!heatOn || !page.step_key) return null;
    const st = heat[page.step_key];
    if (!st) return null;
    const share = st.reached ? st.left_here / st.reached : 0;
    const level = !st.reached ? 'none' : st.kind === 'got' ? 'got' : share >= 0.4 ? 'hot' : share >= 0.15 ? 'warm' : 'cool';
    return { ...st, share, level };
  }

  async function tidy() {
    const { error } = await supabase
      .from('bb_tech_pages')
      .update({ manual_position_x: null, manual_position_y: null })
      .eq('workspace_id', workspace.id);
    if (error) { showToast('Tidy failed: ' + error.message, 'error'); return; }
    await load();
    showToast('Layout tidied', 'success');
  }

  $effect(() => {
    if (registerTidy) registerTidy(tidy);
  });

  const terminalPageIds = $derived.by(() => {
    const set = new Set(visiblePageIds);
    for (const c of choices) {
      if (!visibleChoiceSet.has(c.id)) continue;
      set.delete(c.source_page_id);
    }
    return set;
  });

  function pageHeight(pageId) {
    const count = buttons.filter(b => b.page_id === pageId).length;
    return PAGE_H_BASE + Math.max(0, count) * (BTN_H + 4);
  }

  const visibleChoiceSet = $derived.by(() => {
    let set = new Set(choices.map(c => c.id));
    if (activeJourneyIds.length) {
      set = new Set(choices.filter(c => activeJourneyIds.includes(c.journey_id)).map(c => c.id));
    }
    if (activeButtonIds.length) {
      const filtered = [...set].map(id => choices.find(c => c.id === id)).filter(c => c && activeButtonIds.includes(c.source_button_id));
      set = new Set(filtered.map(c => c.id));
    }
    return set;
  });

  const visiblePageIds = $derived.by(() => {
    if (!activeJourneyIds.length && !activePageIds.length && !activeButtonIds.length) {
      return new Set(pages.map(p => p.id));
    }
    const baseSet = new Set();
    for (const c of choices) {
      if (!visibleChoiceSet.has(c.id)) continue;
      baseSet.add(c.source_page_id);
      baseSet.add(c.target_page_id);
    }
    for (const j of journeys) {
      if (activeJourneyIds.includes(j.id) && j.starting_page_id) baseSet.add(j.starting_page_id);
    }
    if (activePageIds.length) {
      const hopSet = new Set(activePageIds);
      for (const c of choices) {
        if (activePageIds.includes(c.source_page_id)) hopSet.add(c.target_page_id);
        if (activePageIds.includes(c.target_page_id)) hopSet.add(c.source_page_id);
      }
      if (!activeJourneyIds.length && !activeButtonIds.length) {
        return hopSet;
      }
      for (const id of hopSet) baseSet.add(id);
    }
    return baseSet;
  });

  const layout = $derived.by(() => {
    if (pages.length === 0) return { nodes: [], edges: [], width: 0, height: 0 };
    const g = new dagre.graphlib.Graph();
    g.setGraph({ rankdir: 'TB', nodesep: 60, ranksep: 80, marginx: 40, marginy: 40 });
    g.setDefaultEdgeLabel(() => ({}));

    for (const p of pages) {
      if (!visiblePageIds.has(p.id)) continue;
      g.setNode(p.id, { width: PAGE_W, height: pageHeight(p.id) });
    }
    for (const c of choices) {
      if (!visibleChoiceSet.has(c.id)) continue;
      if (!visiblePageIds.has(c.source_page_id) || !visiblePageIds.has(c.target_page_id)) continue;
      g.setEdge(c.source_page_id, c.target_page_id, { id: c.id });
    }

    try { dagre.layout(g); } catch (e) { console.warn('dagre layout failed', e); }

    const nodes = [];
    for (const p of pages) {
      if (!visiblePageIds.has(p.id)) continue;
      const n = g.node(p.id);
      if (!n) continue;
      const x = (p.manual_position_x ?? n.x) - PAGE_W / 2;
      const y = (p.manual_position_y ?? n.y) - pageHeight(p.id) / 2;
      nodes.push({ page: p, x, y, w: PAGE_W, h: pageHeight(p.id) });
    }

    const edges = [];
    for (const c of choices) {
      if (!visibleChoiceSet.has(c.id)) continue;
      const fromNode = nodes.find(n => n.page.id === c.source_page_id);
      const toNode = nodes.find(n => n.page.id === c.target_page_id);
      if (!fromNode || !toNode) continue;
      const journey = journeys.find(j => j.id === c.journey_id);
      const color = journey?.color || '#9aa0a6';
      const pageBtns = buttons.filter(b => b.page_id === c.source_page_id);
      const btnIdx = pageBtns.findIndex(b => b.id === c.source_button_id);
      const buttonY = fromNode.y + PAGE_H_BASE + (btnIdx + 0.5) * (BTN_H + 4);
      const x1 = fromNode.x + PAGE_W;
      const y1 = buttonY;
      const x2 = toNode.x + toNode.w / 2;
      const y2 = toNode.y;
      edges.push({ choice: c, color, x1, y1, x2, y2 });
    }

    let maxX = 0, maxY = 0;
    for (const n of nodes) {
      maxX = Math.max(maxX, n.x + n.w);
      maxY = Math.max(maxY, n.y + n.h);
    }
    return { nodes, edges, width: maxX + 60, height: maxY + 60 };
  });

  function onWheel(e) {
    e.preventDefault();
    const factor = e.deltaY < 0 ? 1.05 : 0.95;
    zoom = Math.max(0.3, Math.min(2, zoom * factor));
  }
  function onMouseDown(e) {
    if (e.button !== 0) return;
    if (e.target.closest('.tc-page') || e.target.closest('.tc-btn-chip') || e.target.closest('.tc-route-editor')) return;
    routeEditor = null;
    isPanning = true;
    panStart = { x: e.clientX, y: e.clientY, panX, panY };
  }

  function onButtonClick(e, page, button, node) {
    e.stopPropagation();
    let jid = null;
    if (activeJourneyIds && activeJourneyIds.length > 0) {
      jid = activeJourneyIds[0];
    } else if (journeys.length === 1) {
      jid = journeys[0].id;
    } else if (journeys.length > 1) {
      const confirmed = journeys.find(j => j.status === 'confirmed');
      jid = (confirmed || journeys[0]).id;
    }
    if (!jid) {
      showToast('Build a journey first', 'error');
      return;
    }
    routeJourneyId = jid;
    const btnIdx = buttons.filter(b => b.page_id === page.id).findIndex(b => b.id === button.id);
    const anchorX = node.x + node.w + 8;
    const anchorY = node.y + PAGE_H_BASE + btnIdx * (BTN_H + 4);
    routeEditor = {
      pageId: page.id,
      buttonId: button.id,
      anchorX, anchorY,
      value: '',
      suggestionIdx: -1
    };
  }

  function closeRouteEditor() {
    routeEditor = null;
  }

  const routeSuggestions = $derived.by(() => {
    if (!routeEditor) return [];
    const q = (routeEditor.value || '').trim().toLowerCase();
    if (q.length < 1) return [];
    return pages.filter(p => p.title.toLowerCase().includes(q) && p.title.toLowerCase() !== q && p.id !== routeEditor.pageId).slice(0, 6);
  });

  async function saveRoute() {
    if (!routeEditor) return;
    const title = (routeEditor.value || '').trim();
    if (!title) return;
    let targetPageId;
    const existingMatch = pages.find(p => p.title.toLowerCase() === title.toLowerCase());
    if (existingMatch) {
      targetPageId = existingMatch.id;
    } else {
      const { data, error } = await supabase.from('bb_tech_pages').insert({
        workspace_id: workspace.id,
        title
      }).select().single();
      if (error) { showToast('Page create failed: ' + error.message, 'error'); return; }
      targetPageId = data.id;
    }
    const existingChoice = choices.find(c =>
      c.journey_id === routeJourneyId &&
      c.source_page_id === routeEditor.pageId &&
      c.source_button_id === routeEditor.buttonId
    );
    if (existingChoice) {
      await supabase.from('bb_tech_choices').update({ target_page_id: targetPageId }).eq('id', existingChoice.id);
    } else {
      await supabase.from('bb_tech_choices').insert({
        workspace_id: workspace.id,
        journey_id: routeJourneyId,
        source_page_id: routeEditor.pageId,
        source_button_id: routeEditor.buttonId,
        target_page_id: targetPageId
      });
    }
    showToast('Route saved', 'success');
    closeRouteEditor();
    await load();
  }

  function onRouteKey(e) {
    if (!routeEditor) return;
    if (e.key === 'ArrowDown' && routeSuggestions.length > 0) {
      e.preventDefault();
      routeEditor.suggestionIdx = (routeEditor.suggestionIdx + 1) % routeSuggestions.length;
    } else if (e.key === 'ArrowUp' && routeSuggestions.length > 0) {
      e.preventDefault();
      routeEditor.suggestionIdx = routeEditor.suggestionIdx <= 0 ? routeSuggestions.length - 1 : routeEditor.suggestionIdx - 1;
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (routeEditor.suggestionIdx >= 0 && routeSuggestions[routeEditor.suggestionIdx]) {
        routeEditor.value = routeSuggestions[routeEditor.suggestionIdx].title;
      }
      saveRoute();
    } else if (e.key === 'Escape') {
      closeRouteEditor();
    }
  }

  function pickRouteSuggestion(p) {
    if (!routeEditor) return;
    routeEditor.value = p.title;
    saveRoute();
  }
  function onMouseMove(e) {
    if (!isPanning) return;
    panX = panStart.panX + (e.clientX - panStart.x);
    panY = panStart.panY + (e.clientY - panStart.y);
  }
  function onMouseUp() { isPanning = false; }

  function fit() {
    zoom = 1;
    panX = 0;
    panY = 0;
  }

  function routeSummary(pageId, buttonId) {
    const relevantChoices = choices.filter(c =>
      c.source_page_id === pageId &&
      c.source_button_id === buttonId &&
      (!activeJourneyIds.length || activeJourneyIds.includes(c.journey_id))
    );
    if (relevantChoices.length === 0) return null;
    return relevantChoices.map(c => {
      const target = pages.find(p => p.id === c.target_page_id);
      return target?.title || '?';
    }).join(', ');
  }

  function buttonTooltip(pageId, b) {
    const parts = [b.name];
    const route = routeSummary(pageId, b.id);
    if (route) parts.push(`→ ${route}`);
    if (b.description) parts.push(b.description);
    return parts.join('\n');
  }

  function colorsForPage(pageId) {
    const set = new Set();
    for (const c of choices) {
      if (!visibleChoiceSet.has(c.id)) continue;
      if (c.source_page_id === pageId || c.target_page_id === pageId) {
        const j = journeys.find(jj => jj.id === c.journey_id);
        if (j?.color) set.add(j.color);
      }
    }
    return [...set];
  }
</script>

<div
  class="tc-wrap"
  onwheel={onWheel}
  onmousedown={onMouseDown}
  onmousemove={onMouseMove}
  onmouseup={onMouseUp}
  onmouseleave={onMouseUp}
>
  {#if loading}
    <div class="tc-loading">Loading…</div>
  {:else if layout.nodes.length === 0}
    <div class="tc-empty">No pages yet — start a journey to build your first one.</div>
  {:else}
    <div class="tc-zoom-controls">
      <button class="tc-z-btn" onclick={() => zoom = Math.min(2, zoom * 1.1)} type="button">+</button>
      <button class="tc-z-btn" onclick={() => zoom = Math.max(0.3, zoom * 0.9)} type="button">−</button>
      <button class="tc-z-btn" onclick={fit} type="button" title="Fit">⊡</button>
      <button class="tc-z-btn" class:on={heatOn} onclick={() => heatOn = !heatOn} type="button" title="Real numbers (last 30 days)">◐</button>
    </div>
    <div class="tc-canvas" style="transform: translate({panX}px, {panY}px) scale({zoom}); transform-origin: 0 0;">
      <svg width={layout.width} height={layout.height} class="tc-svg">
        <defs>
          <marker id="tc-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="8" markerHeight="8" orient="auto">
            <path d="M 0 0 L 10 5 L 0 10 z" fill="currentColor" />
          </marker>
        </defs>
        {#each layout.edges as edge}
          {@const midX = (edge.x1 + edge.x2) / 2}
          {@const pathD = edgeStyle === 'right-angle'
            ? `M ${edge.x1} ${edge.y1} L ${midX} ${edge.y1} L ${midX} ${edge.y2} L ${edge.x2} ${edge.y2}`
            : `M ${edge.x1} ${edge.y1} C ${midX} ${edge.y1}, ${midX} ${edge.y2}, ${edge.x2} ${edge.y2}`}
          <path
            d={pathD}
            stroke={edge.color}
            stroke-width="1.6"
            fill="none"
            marker-end="url(#tc-arrow)"
            style="color: {edge.color}"
          />
        {/each}
      </svg>

      {#each layout.nodes as node}
        {@const pageButtons = buttons.filter(b => b.page_id === node.page.id)}
        {@const pageColors = colorsForPage(node.page.id)}
        {@const isTerminal = terminalPageIds.has(node.page.id)}
        {@const h = heatFor(node.page)}
        <div
          class="tc-page {h ? 'heat-' + h.level : ''}"
          class:terminal={isTerminal}
          onmousedown={(e) => onPageMouseDown(e, node)}
          style="left: {node.x}px; top: {node.y}px; width: {node.w}px; height: {node.h}px;"
        >
          {#if isTerminal}
            <span class="tc-value-badge">VALUE</span>
          {/if}
          <div class="tc-page-header">
            <span class="tc-page-title">{node.page.title}</span>
            <div class="tc-page-color-dots">
              {#each pageColors as c}
                <span class="tc-color-dot" style="background: {c}"></span>
              {/each}
            </div>
          </div>
          <div class="tc-page-body">
            {#if h}
              <span class="tc-heat">{h.reached} reached{#if h.kind !== 'got' && h.reached} · {h.left_here} left ({Math.round(h.share * 100)}%){/if}</span>
            {/if}
            {#if pageButtons.length === 0}
              <span class="tc-no-buttons">No buttons</span>
            {/if}
          </div>
          <div class="tc-page-buttons">
            {#each pageButtons as b}
              {@const hasRouteForActiveJourney = choices.some(c =>
                c.source_page_id === node.page.id &&
                c.source_button_id === b.id &&
                (!activeJourneyIds.length || activeJourneyIds.includes(c.journey_id))
              )}
              <button
                class="tc-btn-chip"
                class:hidden={b.is_hidden}
                class:has-route={hasRouteForActiveJourney}
                onclick={(e) => { e.stopPropagation(); onButtonClick(e, node.page, b, node); }}
                onmousedown={(e) => e.stopPropagation()}
                title={buttonTooltip(node.page.id, b)}
                type="button"
              >
                {b.name}
                {#if hasRouteForActiveJourney}<span class="tc-route-arrow">→</span>{/if}
              </button>
            {/each}
          </div>
        </div>
      {/each}

      {#if routeEditor}
        <div class="tc-route-editor" style="left: {routeEditor.anchorX}px; top: {routeEditor.anchorY}px;" onclick={(e) => e.stopPropagation()}>
          <input
            class="tc-route-input"
            bind:value={routeEditor.value}
            oninput={() => routeEditor.suggestionIdx = -1}
            onkeydown={onRouteKey}
            placeholder="Routes to…"
          />
          {#if routeSuggestions.length > 0}
            <div class="tc-route-suggestions">
              {#each routeSuggestions as s, sIdx}
                <button
                  class="tc-route-suggestion"
                  class:active={sIdx === routeEditor.suggestionIdx}
                  onmousedown={() => pickRouteSuggestion(s)}
                  type="button"
                >{s.title}</button>
              {/each}
            </div>
          {/if}
          <div class="tc-route-hint">Enter to save · Esc to cancel</div>
        </div>
      {/if}
    </div>
  {/if}
</div>

<style>
  .tc-wrap {
    width: 100%; height: 100%;
    position: relative;
    overflow: hidden;
    cursor: grab;
    background: radial-gradient(circle at center, rgba(212,175,55,0.02), transparent 60%);
  }
  .tc-wrap:active { cursor: grabbing; }
  .tc-canvas { position: absolute; top: 0; left: 0; }
  .tc-svg { position: absolute; top: 0; left: 0; pointer-events: none; }
  .tc-loading, .tc-empty {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    color: #9aa0a6;
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    letter-spacing: 1.2px;
  }
  .tc-zoom-controls {
    position: absolute; top: 14px; right: 14px;
    display: flex; gap: 4px;
    z-index: 5;
  }
  .tc-z-btn {
    width: 32px; height: 32px;
    background: rgba(0,0,0,0.6);
    border: 1px solid rgba(255,255,255,0.1);
    color: #d8d8dc;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
  }
  .tc-z-btn:hover { background: rgba(212,175,55,0.15); color: #d4af37; }
  .tc-z-btn.on { color: #d4af37; border-color: rgba(212,175,55,0.4); }
  .tc-heat { display: block; font-family: 'JetBrains Mono', monospace; font-size: 10px; letter-spacing: .3px; color: #d8d8dc; margin-bottom: 4px; }
  .tc-page.heat-cool { border-color: rgba(52,168,110,0.6); background: linear-gradient(180deg, rgba(24,52,38,0.96), rgba(18,17,22,0.96)); }
  .tc-page.heat-warm { border-color: rgba(232,170,60,0.65); background: linear-gradient(180deg, rgba(60,44,18,0.96), rgba(18,17,22,0.96)); }
  .tc-page.heat-hot { border-color: rgba(228,80,106,0.75); background: linear-gradient(180deg, rgba(66,22,30,0.96), rgba(18,17,22,0.96)); }
  .tc-page.heat-got { border-color: rgba(212,175,55,0.7); }
  .tc-page.heat-none .tc-heat { color: #6a6a6e; }
  .tc-page {
    position: absolute;
    background: linear-gradient(180deg, rgba(28,26,32,0.96), rgba(18,17,22,0.96));
    border: 1px solid rgba(140, 130, 110, 0.18);
    border-radius: 12px;
    box-shadow:
      0 0 0 1px rgba(255,255,255,0.02) inset,
      0 6px 18px rgba(0,0,0,0.55),
      0 1px 0 rgba(255,255,255,0.04) inset;
    display: flex; flex-direction: column;
    overflow: hidden;
    cursor: grab;
  }
  .tc-page:active { cursor: grabbing; }
  .tc-page:hover {
    border-color: rgba(180, 165, 130, 0.3);
  }
  .tc-page-header { cursor: inherit; }
  .tc-route-editor { cursor: default; }
  .tc-page-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 10px 12px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    background: rgba(255,255,255,0.02);
  }
  .tc-page-title {
    font-family: 'DM Serif Display', serif;
    font-size: 14px;
    color: #f0efe9;
  }
  .tc-page-color-dots { display: flex; gap: 3px; }
  .tc-color-dot { width: 8px; height: 8px; border-radius: 50%; }
  .tc-page-body {
    flex: 1;
    padding: 10px 12px;
    color: #6a6a6e;
    font-size: 11px;
    font-family: 'JetBrains Mono', monospace;
  }
  .tc-no-buttons { color: #4a4a4e; }
  .tc-page-buttons {
    display: flex; flex-direction: column; gap: 4px;
    padding: 6px 10px 10px;
  }
  .tc-btn-chip {
    background: rgba(212,175,55,0.08);
    border: 1px solid rgba(212,175,55,0.25);
    color: #d4af37;
    padding: 3px 10px;
    border-radius: 6px;
    font-size: 11px;
    font-family: 'JetBrains Mono', monospace;
    text-align: center;
    cursor: pointer;
    display: inline-flex; justify-content: space-between; align-items: center; gap: 6px;
  }
  .tc-btn-chip:hover {
    background: rgba(212,175,55,0.15);
    border-color: rgba(212,175,55,0.45);
  }
  .tc-btn-chip.has-route {
    background: rgba(212,175,55,0.18);
    border-color: rgba(212,175,55,0.5);
  }
  .tc-btn-chip.hidden {
    opacity: 0.4;
    text-decoration: line-through;
  }
  .tc-route-arrow {
    color: #d4af37;
    font-weight: 600;
  }
  .tc-route-editor {
    position: absolute;
    width: 220px;
    background: #0a0a0c;
    border: 1px solid rgba(212,175,55,0.4);
    border-radius: 8px;
    padding: 8px;
    box-shadow: 0 12px 30px rgba(0,0,0,0.6);
    z-index: 20;
  }
  .tc-route-input {
    width: 100%;
    background: rgba(0,0,0,0.4);
    border: 1px solid rgba(255,255,255,0.1);
    color: #f4f4f6;
    padding: 6px 10px;
    border-radius: 6px;
    font-size: 12px;
    font-family: inherit;
  }
  .tc-route-input:focus { outline: none; border-color: rgba(212,175,55,0.5); }
  .tc-route-suggestions {
    margin-top: 4px;
    max-height: 160px;
    overflow-y: auto;
    border: 1px solid rgba(255,255,255,0.06);
    border-radius: 6px;
  }
  .tc-route-suggestion {
    display: block; width: 100%;
    background: none; border: none;
    color: #d8d8dc;
    padding: 5px 10px;
    text-align: left;
    cursor: pointer;
    font-size: 12px;
  }
  .tc-route-suggestion.active, .tc-route-suggestion:hover {
    background: rgba(212,175,55,0.12);
    color: #d4af37;
  }
  .tc-route-hint {
    margin-top: 4px;
    color: #6a6a6e;
    font-size: 9px;
    font-family: 'JetBrains Mono', monospace;
    letter-spacing: 1px;
  }
  .tc-page.terminal {
    border-color: rgba(212, 175, 55, 0.5);
    box-shadow:
      0 0 24px rgba(212, 175, 55, 0.4),
      0 6px 18px rgba(0,0,0,0.55),
      0 1px 0 rgba(255,255,255,0.04) inset;
    animation: tc-value-pulse 2.4s ease-in-out infinite;
  }
  @keyframes tc-value-pulse {
    0%, 100% { box-shadow: 0 0 24px rgba(212, 175, 55, 0.4), 0 6px 18px rgba(0,0,0,0.5); }
    50% { box-shadow: 0 0 36px rgba(212, 175, 55, 0.6), 0 6px 18px rgba(0,0,0,0.5); }
  }
  .tc-value-badge {
    position: absolute;
    top: 8px; right: 8px;
    font-family: 'JetBrains Mono', monospace;
    font-size: 8px;
    letter-spacing: 1.6px;
    color: #d4af37;
    background: rgba(212,175,55,0.15);
    border: 1px solid rgba(212,175,55,0.4);
    padding: 2px 6px;
    border-radius: 4px;
    z-index: 2;
  }
</style>
